'use client'

import { type Media, Project } from '@/payload-types'
import React, { useState } from 'react'
import Link from 'next/link'
import { Subheading } from '@/components/ui/subheading'
import { cn } from '@/utilities/ui'
import { Heading } from '@/components/ui/heading'
import { getCMSLinkHrefAndTabProps } from '@/utilities/getCMSLinkHref'
import RichText from './RichText'
import { Button } from './ui/Button'
import { PixelatedImage } from './PixelatedImage'

export const ProjectCardLarge: React.FC<{
  project: Project
  className?: string
  card?: boolean
  aspectRatio?: '3/4' | '4/3' | '2/1'
  showAuthor?: boolean
  size?: 'md' | 'lg'
  variant?: 'light' | 'dark'
  pixelSize?: number
}> = ({
  project,
  className,
  card = true,
  aspectRatio = '3/4',
  size = 'md',
  variant = 'dark',
  pixelSize = 24,
}) => {
  const [isHovering, setIsHovering] = useState(false)
  const heroImage =
    project.heroImage && typeof project.heroImage === 'object' ? (project.heroImage as Media) : null
  const textClass = variant === 'light' ? 'text-light' : 'text-dark'

  const link = { ...project.link, newTab: true }
  return (
    <Link
      {...getCMSLinkHrefAndTabProps(link)}
      className={cn('group block w-[330px] flex-none', card && 'bg-paper-2', className)}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div
        className={cn(
          'relative overflow-hidden',
          aspectRatio === '3/4' && 'aspect-[3/4]',
          aspectRatio === '4/3' && 'aspect-[4/3]',
          aspectRatio === '2/1' && 'aspect-[2/1]',
        )}
      >
        {heroImage && (
          <PixelatedImage
            src={heroImage.url || ''}
            alt={heroImage.alt || ''}
            aspectRatio={aspectRatio}
            pixelSize={pixelSize}
            defaultState="original"
            isHovering={isHovering}
            className="w-full h-full"
          />
        )}
      </div>
      <div className={cn(card ? 'py-[30px] px-[30px]' : 'pt-[30px]')}>
        <div className="flex flex-col gap-[20px]">
          <div className={cn('flex-1 flex flex-col gap-[20px] items-start')}>
            {size === 'md' ? (
              <Subheading size="md" className={textClass}>
                {project.title}
              </Subheading>
            ) : (
              <Heading size="h3" className={textClass}>
                {project.title}
              </Heading>
            )}
            <RichText
              className={cn(
                'prose-p:!text-body-sm max-w-[800px] text-balance',
                variant === 'light' ? 'prose-light' : 'prose-dark',
                textClass,
              )}
              data={project.content}
            />
            <Button
              size={size === 'md' ? 'clear' : 'default'}
              variant={size === 'md' ? 'link-punch' : 'punch'}
              className="group-hover:underline"
              linkIcon="external"
            >
              Read more
            </Button>
          </div>
        </div>
      </div>
    </Link>
  )
}
